import {
  ExpandableSection,
  Flex,
  PremiumBreakdown,
  PremiumLineItem,
  PremiumSummary,
  smBumps,
  Typo,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useMemo } from 'react'

import { inForce, PriceOption, Quote } from '../../../domain'
import { useQuotePremiums } from '../../../hooks/premiums'
import {
  tFormats,
  TKeyBuilder,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { ProductConfig } from '../../../state/configuration'
import { toDateTime } from '../../../util/date'
import { IPT } from '../product'
import {
  additionalLineItems,
  coreLineItems,
  discountLineItems,
  optionalLineItems,
} from './lineItems'

export interface StandardAdjustedPremiumYearlyProps {
  currentQuote: Quote
  previousQuote?: Quote
  product: ProductConfig
  discount?: PriceOption
  expanded?: boolean
  tKey: TKeyBuilder
  onExpansionChange?: (isExpanded: boolean) => void
}

export const StandardAdjustedPremiumYearly: FunctionComponent<
  StandardAdjustedPremiumYearlyProps
> = ({
  currentQuote,
  previousQuote,
  product,
  discount,
  expanded,
  tKey,
  onExpansionChange,
}) => {
  const { t } = useWriskTranslation()

  const currentPremiums = useQuotePremiums(currentQuote, product, 'SINGLE_PAYMENT')
  const previousPremiums = useQuotePremiums(previousQuote, product, 'SINGLE_PAYMENT')

  const subheader = inForce(discount)
    ? t(tKey('promotion'), {
        date: t(tFormats('date.long'), { value: toDateTime(discount?.endAt) }),
      })
    : undefined

  const header = (
    <PremiumSummary
      header={t<string>(tKey('header'))}
      subheader={subheader}
      currentAmount={t(tFormats('currency.long'), {
        amount: currentPremiums.totals.discounted,
      })}
      previousAmount={
        previousQuote &&
        previousPremiums &&
        t<string>(tFormats('currency.long'), {
          amount: previousPremiums.totals.discounted,
        })
      }
    />
  )

  const lineItems = useMemo<PremiumLineItem[]>(
    () => [
      ...coreLineItems(t, tKey, currentPremiums, previousQuote && previousPremiums),
      ...discountLineItems(t, tKey, currentPremiums, previousQuote && previousPremiums),
      ...optionalLineItems(t, currentPremiums, previousQuote && previousPremiums),
    ],
    [currentPremiums, previousQuote, previousPremiums, t, tKey],
  )

  const additionalLineItemsMemo = useMemo(
    () => additionalLineItems(t, tKey, currentPremiums),
    [currentPremiums, t, tKey],
  )

  const extraInfo = (
    <Flex flexDirection='column' width={1} borderTopWidth={1} py={smBumps} gap={smBumps}>
      <Flex width={1}>
        <Typo typoSize='sm' fontWeight='bold'>
          {t(tKey('header'))}
        </Typo>
        <Typo typoSize='sm' fontWeight='bold'>
          {t(tFormats('currency.long'), {
            amount: currentPremiums.totals.discounted,
          })}
        </Typo>
      </Flex>
    </Flex>
  )

  return (
    <ExpandableSection
      header={header}
      open={expanded}
      reversed
      onChange={onExpansionChange}
    >
      <PremiumBreakdown
        extraInfo={extraInfo}
        lineItems={lineItems}
        additionalLineItems={additionalLineItemsMemo}
        ipt={<IPT amount={currentPremiums.totals.taxAmount} />}
        mx={smBumps}
      />
    </ExpandableSection>
  )
}
